import { useEffect, useState } from 'react';

import { fetchFileDuplicateSummary } from '../../../services/syntiro_honey/data_ingestion.service';
import { FileDuplicateSummary } from '../../../types/syntiro_honey';

/**
 * Fetches a file's duplicate-resolution state — the stored decision plus how many
 * of its active records collide with records already on Syntiro — driving the
 * review screen's resolution banner (Maxine 2026-08-19 scenario 2). Fetched only
 * while the review is scoped to one file; `undefined` clears the state.
 * Re-fetches when the file or `refreshKey` changes (the panel bumps the key on
 * any mutation, so a resolution's record writes refresh the banner too).
 * @param {string | undefined} fileId The scoped file, or undefined when unscoped.
 * @param {number} refreshKey Bump to re-fetch.
 * @returns {FileDuplicateSummary | null} The summary, or null until a load lands.
 */
export function useFileDuplicateSummary(
  fileId: string | undefined,
  refreshKey: number
): FileDuplicateSummary | null {
  const [summary, setSummary] = useState<FileDuplicateSummary | null>(null);

  useEffect(() => {
    if (fileId === undefined) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    // The previous file's summary is not this file's: clear it before fetching so
    // a scope switch never shows (or resolves against) stale counts.
    setSummary(null);
    fetchFileDuplicateSummary(fileId)
      .then((result) => {
        if (!cancelled) {
          setSummary(result);
        }
      })
      // The banner is an extra; a failed summary (e.g. an older backend without
      // the endpoint) simply shows no banner rather than erroring the review page.
      .catch(() => {
        if (!cancelled) {
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileId, refreshKey]);

  return summary;
}
